import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { UploadFileProgress } from 'modules/files';
import spinnerTitle from 'components/others/spinnerTitle';
import AiInfoBlock from 'components/others/AiInfoBlock';
import SolarIcon from 'components/icons/SolarIcon';
import { useModal } from 'context/ModalContext';
import { useToast } from 'context/ToastContext';
import { getButtonsNoYes } from 'context/helpers/helperModalContext';
import type { TemplateDocAiResultType } from '@econtractor/types';
import useTemplateDocAi from './model/useTemplateDocAi';

export interface TemplateDocAiApplyPatch {
    proposalTitle?: string;
    proposalTitlePage?: boolean;
    proposalIntro: string;
    proposalOutro: string;
}

interface AIDocProps {
    disabled?: boolean;
    includeTitle?: boolean;
    proposalTitle?: string;
    proposalIntro: string;
    proposalOutro: string;
    onApply: (patch: TemplateDocAiApplyPatch) => void;
}

const isEmptyHtml = (value: string): boolean =>
    !String(value || '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\u00a0/g, ' ')
        .trim();

const AIDoc = ({
    disabled = false,
    includeTitle = false,
    proposalTitle = '',
    proposalIntro,
    proposalOutro,
    onApply,
}: AIDocProps) => {
    const { t } = useTranslation(['TemplateValueKeys', 'common']);
    const { showDlg } = useModal();
    const { showToast } = useToast();

    const applyResult = (result: TemplateDocAiResultType) => {
        const patch: TemplateDocAiApplyPatch = {
            proposalIntro: result.proposalIntro,
            proposalOutro: result.proposalOutro,
        };
        if (includeTitle && result.proposalTitle.trim()) {
            patch.proposalTitle = result.proposalTitle;
            patch.proposalTitlePage = true;
        }
        onApply(patch);
        showToast(t('aiDoc.messLoaded'), 'success');
    };

    const handleParsed = (result: TemplateDocAiResultType) => {
        const hasExisting =
            !isEmptyHtml(proposalIntro)
            || !isEmptyHtml(proposalOutro)
            || (includeTitle && !isEmptyHtml(proposalTitle));

        if (!hasExisting) {
            applyResult(result);
            return;
        }

        const buttons = [...getButtonsNoYes(t)];
        buttons[1].handleClick = () => {
            showDlg(false);
            applyResult(result);
        };
        showDlg({
            type: 'confirm',
            title: t('aiDoc.confirmTitle'),
            text: t('aiDoc.confirmText'),
            buttons,
        });
    };

    const {
        isBusy,
        phase,
        uploadProgress,
        processingLabel,
        refInputFile,
        handleOpenClick,
        handleCancel,
        handleChangeFile,
    } = useTemplateDocAi({ onParsed: handleParsed });

    return (
        <AiInfoBlock title={t('aiDoc.title')}>
            <p>{t('aiDoc.info')}</p>
            <div className="d-flex flex-wrap gap-2">
                <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleOpenClick}
                    disabled={disabled || isBusy}
                >
                    {spinnerTitle(
                        <>
                            <SolarIcon name="stars" className="me-1" />
                            {t('aiDoc.openFile')}
                        </>,
                        isBusy,
                        processingLabel,
                    )}
                </Button>
                {phase === 'processing' && (
                    <Button type="button" variant="outline-danger" size="sm" onClick={handleCancel}>
                        <SolarIcon name="stop-circle" className="me-1" />
                        {t('aiDoc.cancel')}
                    </Button>
                )}
            </div>
            {uploadProgress !== false && (
                <div className="pt-2">
                    <UploadFileProgress progress={Number(uploadProgress)} />
                    <div className="small text-muted mt-2">{processingLabel}</div>
                </div>
            )}
            <input
                ref={refInputFile}
                type="file"
                className="d-none"
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.gif,application/pdf,image/*"
                onChange={() => {
                    void handleChangeFile();
                }}
            />
        </AiInfoBlock>
    );
};

export default AIDoc;
